<template>
  <div class="guide-hubspot">
    <p class="guide-lead">
      The eSign Launchpad HubSpot Marketplace integration triggers signing
      packages directly from HubSpot Workflows, threads lifecycle events
      back onto matched Contacts, and (optionally) attaches the signed
      PDF to the Contact's Files and attachments tab. Built for sales
      teams who don't want to leave HubSpot to send a contract.
      Marketplace listing is in HubSpot review; Connect HubSpot in your
      eSign Launchpad portal works against the OAuth flow today.
    </p>

    <q-separator class="q-my-lg" />

    <h2>What ships in the integration</h2>
    <ul>
      <li>
        <strong>Four workflow actions</strong> usable on any Contact, Deal,
        or Ticket workflow trigger: Send Document for Signature, Void
        eSign Package, Send eSign Reminder, Get eSign Package Status.
      </li>
      <li>
        <strong>Lifecycle timeline events</strong> on matched HubSpot
        Contacts: Sent, Signer Completed, Signer Declined, Voided
        (gated on HubSpot's V4 timeline approval; activates per tenant).
      </li>
      <li>
        <strong>Signed-PDF attachment</strong> on the matched Contact's
        Files and attachments tab when the package completes (gated on
        the file scopes; activates per tenant).
      </li>
      <li>
        <strong>Up to 3 signers per workflow action</strong>. Each role
        maps to a Contact or Deal property at workflow-configuration
        time; the action validates role names against your template
        before sending.
      </li>
      <li>
        <strong>Recency-ordered template dropdown</strong> in the workflow
        editor showing your active templates with their last-used date.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Prerequisites</h2>
    <ul>
      <li>
        A <strong>HubSpot account</strong>. Free CRM works for the Send /
        Void / Reminder / Status actions on Contact and Ticket
        workflows. Deal-based workflows require Sales Hub Professional or
        higher; that's a HubSpot platform constraint, not ours.
      </li>
      <li>
        An <strong>eSign Launchpad portal account</strong> on any plan.
        Connection happens via OAuth, so the install inherits the
        connecting user's tenant and role.
      </li>
      <li>
        At least one <strong>active eSign Launchpad template</strong> in
        the connected tenant. Templates without an active state won't
        appear in the workflow action's template dropdown.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Installing</h2>
    <ol>
      <li>
        Open <a href="https://www.esignlaunchpad.com/Settings/Integrations">Settings → Integrations</a>
        in the eSign Launchpad portal.
      </li>
      <li>
        On the HubSpot card, click <strong>Connect HubSpot</strong> (or
        <em>Connect another portal</em> if you've already linked one).
      </li>
      <li>
        HubSpot's OAuth picker opens. Select the HubSpot account to
        link. If you don't see the right account, sign in to HubSpot in
        another tab and reload.
      </li>
      <li>
        Review the requested scopes and click <strong>Connect app</strong>.
        The current scope set is minimal: <code>oauth</code>,
        <code>crm.objects.contacts.read</code>,
        <code>crm.objects.deals.read</code>, and <code>timeline</code>.
      </li>
      <li>
        On unverified-app warnings (shown until our Marketplace listing
        completes review), type <code>I accept the risk</code> in the
        confirmation field and click <strong>Connect</strong>.
      </li>
      <li>
        HubSpot redirects you to eSign Launchpad's callback. The card
        flips to <strong>Connected</strong> with the install timestamp
        and a Disconnect button.
      </li>
    </ol>

    <q-separator class="q-my-lg" />

    <h2>Workflow actions</h2>

    <h3>Send Document for Signature</h3>
    <p>
      Use case: trigger an envelope automatically when a Deal moves
      stage, a Contact subscribes to a list, or a Ticket reaches a
      certain status.
    </p>
    <table class="guide-table">
      <thead>
        <tr>
          <th>Input field</th>
          <th>Required</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Template</strong></td>
          <td>Required</td>
          <td>Live dropdown of active templates on your tenant, sorted by last-used recency. Pick one.</td>
        </tr>
        <tr>
          <td><strong>Signer 1 Role / Email / Name</strong></td>
          <td>Required</td>
          <td>Role must match a role configured on the template. Email and Name are wired to a Contact or Deal property at configuration time.</td>
        </tr>
        <tr>
          <td><strong>Signer 2 / Signer 3 Role / Email / Name</strong></td>
          <td>Optional</td>
          <td>Each slot is all-or-nothing. Half-populated slots reject. For templates with more than 3 roles, configure the additional roles directly on the template.</td>
        </tr>
        <tr>
          <td><strong>Email Subject / Email Message</strong></td>
          <td>Optional</td>
          <td>Override the template defaults for this specific send.</td>
        </tr>
      </tbody>
    </table>
    <p>
      <strong>Output</strong>: <code>packageId</code>, <code>packageStatus</code>,
      <code>packageName</code>, <code>signerCount</code>, plus per-slot
      <code>signerNEmail / signerNName / signerNRole</code>. Wire these
      to downstream workflow steps that branch on package state or
      address signers individually.
    </p>

    <h3>Void eSign Package</h3>
    <p>
      Use case: void in-flight signature requests when a Deal moves to
      Closed Lost. Pass the <code>packageId</code> from the Send action's
      output and an optional reason that lands on the audit trail.
    </p>

    <h3>Send eSign Reminder</h3>
    <p>
      Use case: re-notify pending signers after a delay branch. Reads
      the package's pending-signer set and re-dispatches the signing
      invitation. Output reports how many reminders were sent.
    </p>

    <h3>Get eSign Package Status</h3>
    <p>
      Use case: branch the workflow on the package's current state
      (Sent / Completed / Voided / Declined / Expired). Pure read,
      no side effects.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Wiring a typical workflow</h2>
    <ol>
      <li>
        In HubSpot, create a Deal-based workflow with the trigger
        <em>Deal stage = Contract Sent</em>.
      </li>
      <li>
        Add the <strong>Send Document for Signature</strong> action.
        Pick your contract template; wire <em>Signer 1 Email</em> to
        <code>Deal &gt; Primary contact email</code> and <em>Signer 1 Name</em>
        to <code>Deal &gt; Primary contact name</code>.
      </li>
      <li>
        Optional: add a <strong>Delay</strong> step (e.g. 3 days) followed
        by a <strong>Send eSign Reminder</strong> action wired to the
        first action's <code>packageId</code> output.
      </li>
      <li>
        For workflows that should branch on signing outcome, add a
        <strong>Get eSign Package Status</strong> action after another
        delay and wire If/Then branches on its <code>packageStatus</code>
        output.
      </li>
      <li>
        Activate the workflow. The next Deal that hits Contract Sent
        triggers the envelope. Test with the <strong>Test workflow</strong>
        button on a sample Deal first.
      </li>
    </ol>

    <q-separator class="q-my-lg" />

    <h2>Privacy posture: read-or-skip on Contacts</h2>
    <p>
      Lifecycle events and signed-PDF attachments only land on HubSpot
      Contacts that already exist. If a signer's email doesn't match a
      Contact, that signer is silently skipped, never auto-created. Your
      CRM stays clean. This is an explicit policy, not a coincidence;
      auto-creation is forbidden in the integration's design.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Security</h2>
    <ul>
      <li>
        Every HubSpot-to-eSign workflow request carries an
        <code>X-HubSpot-Signature-v3</code> header (HMAC-SHA256 over
        method + URL + body + timestamp). We validate it on every
        request before any business logic runs. Mismatched signatures
        are rejected with HTTP 401. Replay protection: requests older
        than 5 minutes are rejected.
      </li>
      <li>
        OAuth tokens are stored AES-256-GCM encrypted with a key kept in
        Azure Key Vault. Never logged.
      </li>
      <li>
        A token-refresh job runs every 15 minutes and rotates expiring
        tokens. Rotated tokens re-encrypt before persistence.
      </li>
      <li>
        On uninstall (HubSpot side or eSign side), tokens invalidate and
        all background calls stop within 30 minutes.
      </li>
      <li>
        Hosted on Microsoft Azure US infrastructure with TLS 1.3 in
        transit and AES-256 at rest.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Disconnecting</h2>
    <p>Two equivalent paths:</p>
    <ul>
      <li>
        <strong>From eSign Launchpad</strong>: Settings → Integrations →
        HubSpot card → Disconnect on the portal you want to remove.
      </li>
      <li>
        <strong>From HubSpot</strong>: Settings → Integrations →
        Connected Apps → Uninstall on the eSign Launchpad app.
      </li>
    </ul>
    <p>
      Either side flows tokens through to invalidation. Workflow runs
      that fire after disconnect fail with a clear "not installed"
      error and stop processing.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Troubleshooting</h2>
    <table class="guide-table">
      <thead>
        <tr>
          <th>Symptom</th>
          <th>Likely cause</th>
          <th>Fix</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Template dropdown is empty in the workflow editor.</td>
          <td>No active templates on the connected tenant, or the install was disconnected.</td>
          <td>Activate at least one template on the eSign Launchpad portal under Templates. Confirm the HubSpot card on Settings → Integrations shows Connected.</td>
        </tr>
        <tr>
          <td>Workflow run shows <code>error: not_installed</code>.</td>
          <td>The HubSpot install was disconnected after the workflow was activated.</td>
          <td>Reconnect the portal (Settings → Integrations → Connect another portal) and re-run the workflow.</td>
        </tr>
        <tr>
          <td>Workflow run shows <code>error: unknown_role</code>.</td>
          <td>The Role Name on a signer slot doesn't match the template.</td>
          <td>Open the template on eSign Launchpad. Copy the exact role name into the workflow action; role matching is case-insensitive but exact-text. The error message lists every valid role on the template.</td>
        </tr>
        <tr>
          <td>Workflow run shows <code>error: missing_signers</code>.</td>
          <td>The template has more roles than the workflow has Signer slots populated.</td>
          <td>Either populate additional Signer 2 / Signer 3 slots, or trim the template to fewer roles.</td>
        </tr>
        <tr>
          <td>Lifecycle events not appearing on Contact timelines.</td>
          <td>Timeline writes are gated on HubSpot's per-app V4 approval; activates per tenant.</td>
          <td>Email <a href="mailto:mail@esignlaunchpad.com">mail@esignlaunchpad.com</a> with your portal id and we'll confirm activation status. Approval is HubSpot-side; we have no fast path.</td>
        </tr>
        <tr>
          <td>Signed PDF not landing on Contact's Files tab.</td>
          <td>Files-API + Notes scopes are gated on a separate scope expansion that forces re-install.</td>
          <td>Same as above; this is a planned activation with reinstall coordination.</td>
        </tr>
      </tbody>
    </table>

    <q-separator class="q-my-lg" />

    <h2>Support</h2>
    <ul>
      <li>Email: <a href="mailto:mail@esignlaunchpad.com">mail@esignlaunchpad.com</a></li>
      <li>Marketing landing page: <a href="https://www.esignlaunchpad.com/integrations/hubspot">/integrations/hubspot</a></li>
      <li>HubSpot Connected Apps in your portal: Settings → Integrations → Connected Apps</li>
    </ul>
  </div>
</template>

<script>
export default {
    name: 'GuideHubspot'
};
</script>

<style scoped>
.guide-lead {
    font-size: 1.1rem;
    line-height: 1.7;
    color: #334155;
}

.guide-table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
}

.guide-table th,
.guide-table td {
    border: 1px solid #e2e8f0;
    padding: 8px 12px;
    text-align: left;
    vertical-align: top;
    line-height: 1.5;
}

.guide-table th {
    background: #f8fafc;
    color: #334155;
    font-weight: 600;
}
</style>
